import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../../components/Modal';
import Button from '../../../components/Button';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { questsService, type ChatQuestTestController, type DisplayItem } from '../services/quests-service';
import {
    saveTestRun,
    getTestRunsForQuest,
    formatTimestamp,
    type SavedTestRun,
} from '../utils/testStorageUtils';

interface TurnGroup {
    turnIndex: number | undefined;
    items: DisplayItem[];
}

const groupItemsByTurn = (items: DisplayItem[]): TurnGroup[] => {
    const groups: TurnGroup[] = [];
    let currentGroup: TurnGroup | null = null;

    for (const item of items) {
        const turnIndex = item.turnIndex;

        if (currentGroup && currentGroup.turnIndex === turnIndex && turnIndex != null) {
            currentGroup.items.push(item);
        } else {
            currentGroup = { turnIndex, items: [item] };
            groups.push(currentGroup);
        }
    }

    return groups;
};

interface ChatQuestAiTestProps {
    isOpen: boolean;
    onClose: () => void;
    journeyDayId: string;
    questTemplateId: string;
    questType: 'chat' | 'check-in';
}

type TestState = 'idle' | 'streaming' | 'success' | 'error' | 'stopped';

const DEFAULT_MAX_TURNS_CHAT = 50;
const DEFAULT_MAX_TURNS_CHECK_IN = 3;

export const ChatQuestAiTest = ({ isOpen, onClose, journeyDayId, questTemplateId, questType }: ChatQuestAiTestProps) => {
    const defaultMaxTurns = questType === 'check-in' ? DEFAULT_MAX_TURNS_CHECK_IN : DEFAULT_MAX_TURNS_CHAT;
    const maxAllowedTurns = questType === 'check-in' ? DEFAULT_MAX_TURNS_CHAT : DEFAULT_MAX_TURNS_CHAT;

    const [state, setState] = useState<TestState>('idle');
    const [conversation, setConversation] = useState<DisplayItem[]>([]);
    const [error, setError] = useState<string>('');
    const [totalTurns, setTotalTurns] = useState<number>(0);
    const [extraGuidance, setExtraGuidance] = useState<string>('Give very short answers');
    const [maxTurns, setMaxTurns] = useState<number>(defaultMaxTurns);
    const testControllerRef = useRef<ChatQuestTestController | null>(null);
    const conversationEndRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const conversationRef = useRef<DisplayItem[]>([]);
    const [userHasScrolledUp, setUserHasScrolledUp] = useState<boolean>(false);
    const [savedTests, setSavedTests] = useState<SavedTestRun[]>([]);
    const [showSavedTests, setShowSavedTests] = useState<boolean>(false);
    const [isLoadedTest, setIsLoadedTest] = useState<boolean>(false);

    // Keep conversationRef in sync with conversation state
    useEffect(() => {
        conversationRef.current = conversation;
    }, [conversation]);

    // Load saved tests from local storage
    const loadSavedTests = useCallback(() => {
        const tests = getTestRunsForQuest(questTemplateId);
        setSavedTests(tests);
    }, [questTemplateId]);

    // Load saved tests when modal opens
    useEffect(() => {
        if (isOpen) {
            loadSavedTests();
        }
    }, [isOpen, loadSavedTests]);

    // Auto-scroll to bottom when new messages arrive (only if user hasn't scrolled up)
    useEffect(() => {
        if (conversationEndRef.current && !userHasScrolledUp) {
            conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversation, userHasScrolledUp]);

    // Handle scroll events to detect if user scrolled up manually
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // If user is within 50px of the bottom, consider them at the bottom
        const isAtBottom = distanceFromBottom < 50;

        setUserHasScrolledUp(!isAtBottom);
    }, []);

    // Reset scroll tracking when starting a new conversation
    useEffect(() => {
        if (state === 'idle' || state === 'streaming') {
            setUserHasScrolledUp(false);
        }
    }, [state]);

    // Save the current test run to local storage
    const saveCurrentTestRun = useCallback(
        (finalState: 'success' | 'error' | 'stopped', turns: number, conversationSnapshot: DisplayItem[], errorMessage?: string) => {
            saveTestRun({
                questTemplateId,
                extraGuidance,
                maxTurns,
                questType,
                conversation: conversationSnapshot,
                totalTurns: turns,
                state: finalState,
                error: errorMessage,
            });

            // Reload saved tests after saving
            loadSavedTests();
        },
        [questTemplateId, extraGuidance, maxTurns, questType, loadSavedTests]
    );

    const runTest = useCallback(async () => {
        setState('streaming');
        setError('');
        setConversation([]);
        setTotalTurns(0);

        let currentConversation: DisplayItem[] = [];

        const handleMessage = (item: DisplayItem) => {
            currentConversation = [...currentConversation, item];
            setConversation(currentConversation);
        };

        const handleComplete = (turns: number) => {
            setTotalTurns(turns);
            setState('success');
            testControllerRef.current = null;

            // Save the successful test run with the latest conversation
            saveCurrentTestRun('success', turns, currentConversation);
        };

        const handleError = (errorMessage: string) => {
            setError(errorMessage);
            setState('error');
            testControllerRef.current = null;

            // Save the failed test run with the conversation up to the error
            saveCurrentTestRun('error', 0, currentConversation, errorMessage);
        };

        const controller = await questsService.testChatQuestStream(
            journeyDayId,
            questTemplateId,
            extraGuidance,
            maxTurns,
            questType,
            handleMessage,
            handleComplete,
            handleError
        );

        testControllerRef.current = controller;
    }, [journeyDayId, questTemplateId, extraGuidance, maxTurns, questType, saveCurrentTestRun]);

    const handleStop = useCallback(async () => {
        if (testControllerRef.current) {
            await testControllerRef.current.stop();
            testControllerRef.current = null;
            setState('stopped');

            // Save the stopped test run with the current conversation state
            saveCurrentTestRun('stopped', 0, conversationRef.current);
        }
    }, [saveCurrentTestRun]);

    const handleExtraGuidanceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setExtraGuidance(e.target.value);
    };

    const handleMaxTurnsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 1 && value <= maxAllowedTurns) {
            setMaxTurns(value);
        }
    };

    const handleShowConfiguration = () => {
        setState('idle');
        setConversation([]);
        setError('');
        setTotalTurns(0);
        setShowSavedTests(false);
        setIsLoadedTest(false);
    };

    const handleLoadSavedTest = useCallback((test: SavedTestRun) => {
        setExtraGuidance(test.extraGuidance);
        setMaxTurns(test.maxTurns);
        setConversation(test.conversation);
        setTotalTurns(test.totalTurns);
        setState(test.state);
        setError(test.error || '');
        setShowSavedTests(false);
        setIsLoadedTest(true);
    }, []);

    const handleBackToTestSelection = () => {
        setState('idle');
        setConversation([]);
        setError('');
        setTotalTurns(0);
        setIsLoadedTest(false);
        setShowSavedTests(true);
    };

    // Cleanup on unmount or close
    useEffect(() => {
        return () => {
            if (testControllerRef.current) {
                testControllerRef.current.stop();
            }
        };
    }, []);

    const handleClose = async () => {
        // Save the conversation if we're closing during an active test
        if (state === 'streaming' && conversationRef.current.length > 0) {
            saveCurrentTestRun('stopped', 0, conversationRef.current);
        }

        if (testControllerRef.current) {
            await testControllerRef.current.stop();
            testControllerRef.current = null;
        }
        setState('idle');
        setConversation([]);
        setError('');
        setTotalTurns(0);
        setExtraGuidance('');
        setMaxTurns(defaultMaxTurns);
        setShowSavedTests(false);
        setIsLoadedTest(false);
        onClose();
    };

    const formatDuration = (ms: number): string => {
        if (ms < 1000) {
            return `${ms}ms`;
        }
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const renderBackToTestsButton = () => {
        if (!isLoadedTest || savedTests.length <= 1) return null;

        return (
            <Button onClick={handleBackToTestSelection} variant="secondary" size="sm">
                ← Back to Tests
            </Button>
        );
    };

    const renderToolCallBadge = (item: DisplayItem & { type: 'tool_call' }, key: string) => (
        <div key={key} className="bg-amber-50 border border-amber-200 rounded px-2 py-1">
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-amber-700">🔧 {item.name}</span>
                <span className="text-xs font-mono text-amber-600">⏱️ {formatDuration(item.durationMs)}</span>
            </div>
            <div className="text-xs text-amber-600 font-mono break-all mt-0.5">{item.arguments}</div>
        </div>
    );

    const renderMessageBubble = (item: DisplayItem & { type: 'message' }, key: string) => {
        const isQuestAi = item.role === 'quest_ai';
        return (
            <div key={key} className={`flex ${isQuestAi ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2 ${isQuestAi ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-900'}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold">{isQuestAi ? '🤖 Quest AI' : '👤 Tester'}</span>
                        <span className={`text-xs font-mono ${isQuestAi ? 'text-blue-600' : 'text-gray-500'}`}>
                            ⏱️ {formatDuration(item.durationMs)}
                        </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{item.content}</div>
                </div>
            </div>
        );
    };

    const turnGroups = useMemo(() => groupItemsByTurn(conversation), [conversation]);

    const renderTurnGroup = (group: TurnGroup, groupIndex: number) => {
        const toolCalls = group.items.filter((i): i is DisplayItem & { type: 'tool_call' } => i.type === 'tool_call');
        const messages = group.items.filter((i): i is DisplayItem & { type: 'message' } => i.type === 'message');

        if (group.turnIndex == null) {
            return group.items.map((item, i) => {
                const key = `ungrouped-${groupIndex}-${i}`;
                if (item.type === 'tool_call') return renderToolCallBadge(item, key);
                if (item.type === 'message') return renderMessageBubble(item, key);
                return null;
            });
        }

        return (
            <div key={`turn-${group.turnIndex}`} className="border-l-2 border-t-1 border-gray-200 pt-2 pl-3 space-y-2">
                <div className="text-xs text-gray-400 font-mono">Turn {group.turnIndex + 1}</div>
                {toolCalls.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {toolCalls.map((tc, i) => renderToolCallBadge(tc, `turn-${group.turnIndex}-tc-${i}`))}
                    </div>
                )}
                {messages.map((msg, i) => renderMessageBubble(msg, `turn-${group.turnIndex}-msg-${i}`))}
            </div>
        );
    };

    const renderStatusBar = () => {
        if (state === 'streaming') {
            return (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <span className="text-sm text-gray-600">Streaming conversation...</span>
                    </div>
                    <Button onClick={handleStop} variant="danger" size="sm">
                        Stop
                    </Button>
                </div>
            );
        }

        if (state === 'error') {
            return (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <ErrorDisplay message={error} />
                    <div className="flex gap-2">
                        {renderBackToTestsButton()}
                        <Button onClick={runTest} variant="primary" size="sm">
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }

        if (state === 'stopped') {
            return (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-amber-600">
                        <span className="text-lg">⏹️</span>
                        <span className="text-sm font-medium">Conversation stopped by user</span>
                    </div>
                    <div className="flex gap-2">
                        {renderBackToTestsButton()}
                        <Button onClick={handleShowConfiguration} variant="primary" size="sm">
                            Restart
                        </Button>
                    </div>
                </div>
            );
        }

        if (state === 'success') {
            return (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm text-gray-500">Conversation completed with {totalTurns} messages</div>
                    <div className="flex gap-2">
                        {renderBackToTestsButton()}
                        <Button onClick={handleShowConfiguration} variant="secondary" size="sm">
                            Run Again
                        </Button>
                    </div>
                </div>
            );
        }

        return null;
    };

    const renderEndMarker = () => {
        if (state === 'streaming') return null;

        const endConfig: Record<Exclude<TestState, 'idle' | 'streaming'>, { icon: string; text: string; bgColor: string; textColor: string }> = {
            success: { icon: '✅', text: 'Conversation completed successfully', bgColor: 'bg-green-50', textColor: 'text-green-700' },
            error: { icon: '❌', text: 'Conversation ended with an error', bgColor: 'bg-red-50', textColor: 'text-red-700' },
            stopped: { icon: '⏹️', text: 'Conversation stopped by user', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
        };

        const config = endConfig[state as Exclude<TestState, 'idle' | 'streaming'>];
        if (!config) return null;

        return (
            <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg ${config.bgColor} ${config.textColor}`}>
                <span>{config.icon}</span>
                <span className="text-sm font-medium">{config.text}</span>
            </div>
        );
    };

    const renderLoadedTestIndicator = () => {
        if (!isLoadedTest || savedTests.length <= 1) return null;

        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-green-700 text-sm font-medium">📂 Viewing loaded test</span>
                    </div>
                    {renderBackToTestsButton()}
                </div>
            </div>
        );
    };

    const renderIdleState = () => {
        return (
            <div className="space-y-4">
                {/* Loaded Test Indicator */}
                {renderLoadedTestIndicator()}

                {/* Saved Tests Section */}
                {savedTests.length > 0 && !isLoadedTest && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-blue-900">Previous Test Runs</h3>
                            <Button
                                onClick={() => setShowSavedTests(!showSavedTests)}
                                variant="secondary"
                                size="sm"
                            >
                                {showSavedTests ? 'Hide' : `Show (${savedTests.length})`}
                            </Button>
                        </div>

                        {showSavedTests && (
                            <div className="space-y-2 mt-3">
                                {savedTests.map((test) => (
                                    <div
                                        key={test.id}
                                        className="bg-white border border-blue-200 rounded-lg p-3 hover:border-blue-400 transition-colors cursor-pointer"
                                        onClick={() => handleLoadSavedTest(test)}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <span
                                                        className={`text-xs font-semibold ${test.state === 'success'
                                                            ? 'text-green-600'
                                                            : test.state === 'error'
                                                                ? 'text-red-600'
                                                                : 'text-amber-600'
                                                            }`}
                                                    >
                                                        {test.state === 'success'
                                                            ? '✅ Success'
                                                            : test.state === 'error'
                                                                ? '❌ Error'
                                                                : '⏹️ Stopped'}
                                                    </span>
                                                    <span className="text-xs text-gray-500">•</span>
                                                    <span className="text-xs text-gray-500">{formatTimestamp(test.timestamp)}</span>
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                    {test.conversation.filter((c) => c.type === 'message').length} messages
                                                    {test.extraGuidance && (
                                                        <span className="ml-2 text-gray-400">
                                                            {test.extraGuidance}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLoadSavedTest(test);
                                                }}
                                                className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors flex-shrink-0"
                                            >
                                                Load
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <label htmlFor="extraGuidance" className="block text-sm font-medium text-gray-700 mb-2">
                        Extra Guidance for Tester AI (optional)
                    </label>
                    <textarea
                        id="extraGuidance"
                        value={extraGuidance}
                        onChange={handleExtraGuidanceChange}
                        placeholder="Enter any additional instructions or context for the tester AI... (e.g., 'Respond as someone who is skeptical about therapy' or 'Give very short answers')"
                        className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        This guidance will be passed to the tester AI to influence how it responds during the conversation.
                    </p>
                </div>
                <div>
                    <label htmlFor="maxTurns" className="block text-sm font-medium text-gray-700 mb-2">
                        Max Conversation Turns
                    </label>
                    <input
                        id="maxTurns"
                        type="number"
                        min={1}
                        max={maxAllowedTurns}
                        value={maxTurns}
                        onChange={handleMaxTurnsChange}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        Maximum number of conversation turns before stopping (1-{maxAllowedTurns}).
                    </p>
                </div>
                <div className="flex justify-end">
                    <Button onClick={runTest} variant="primary">
                        Start Conversation
                    </Button>
                </div>
            </div>
        );
    };

    const renderConversation = () => {
        if (state === 'idle') {
            return renderIdleState();
        }

        if (state === 'streaming' && conversation.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-pulse text-gray-400">Waiting for first message...</div>
                </div>
            );
        }

        const messageCountLabel =
            state === 'stopped' ? (
                <div className="text-sm text-gray-500 mb-2">
                    Partial conversation ({conversation.filter((c) => c.type === 'message').length} messages):
                </div>
            ) : state === 'error' && conversation.length > 0 ? (
                <div className="text-sm text-gray-500 mb-2">Conversation before error:</div>
            ) : null;

        return (
            <div className="space-y-3">
                {messageCountLabel}
                {turnGroups.map((group, index) => renderTurnGroup(group, index))}
                <div ref={conversationEndRef} />
            </div>
        );
    };

    const modalTitle = questType === 'check-in' ? 'AI Check-In Test' : 'AI Chat Quest Test';

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} className="max-h-[70vh] flex flex-col overflow-hidden">
            {state !== 'idle' && (
                <div className="sticky top-0 bg-white pb-4 border-b border-gray-200 mb-4 z-10">{renderStatusBar()}</div>
            )}
            <div ref={scrollContainerRef} onScroll={handleScroll} className="overflow-y-auto flex-1">
                {renderConversation()}
            </div>
            {state !== 'idle' && state !== 'streaming' && (
                <div className="pt-4 border-t border-gray-200 mt-4">{renderEndMarker()}</div>
            )}
        </Modal>
    );
};
